import { styled } from 'styled-components';

import { theme } from '../../styles';
import { extendChildrenWithProps } from '../../utils';
import type { Colors } from '../../utils/types';
import { Container, Flex } from '../Layout';
import { Line } from '../Line';
import { useMediaMax } from '../Media';
import { Text } from '../Text';

type SummaryDetailsProps = {
  variant?: 'light' | 'dark';
  gutterRows?: number;
  wrap?: boolean;
  children: React.ReactNode;
};
const pxToRem = (pxValue: number | string) => {
  if (typeof pxValue === 'string' && /^-?\d*px$/.test(pxValue)) {
    pxValue.replace('px', '');
  }
  return Number(pxValue) ? `${Math.round((Number(pxValue) / 16) * 100_000) / 100_000}rem` : 0;
};
const SummaryDetailsRow = styled(Flex)<{ gutterRows: number }>`
  &:not(:last-child) {
    ${props => props.gutterRows && `margin-bottom: ${pxToRem(props.gutterRows)}`};
  }
`;

const LineWrapper = styled(Flex)`
  align-self: center;
`;

export const SummaryDetails = ({ children, variant, gutterRows = 10, wrap = true }: SummaryDetailsProps) => {
  const isLight = variant === 'light';

  return (
    <Container
      paddingTop={isLight ? [20, 40, 40] : 0}
      paddingRight={isLight ? [20, 40, 40] : 0}
      paddingBottom={isLight ? [40, 60, 50] : 0}
      paddingLeft={isLight ? [20, 40, 40] : 0}
      backgroundColor={isLight ? 'black05' : 'darkBlue'}
    >
      {extendChildrenWithProps(children, { gutterRows, isLight, wrap })}
    </Container>
  );
};

// use SummaryDetails.Title only when variant="ligth", but it can be more flexible in the future
SummaryDetails.Title = ({ children }: any) => {
  const isMobile = useMediaMax(theme.breakpoint.md);

  return (
    <Text marginBottom={20} style={{ fontSize: isMobile ? '1.125rem' : '1rem' }}>
      {children}
    </Text>
  );
};

type SummaryDetailsRowProps = any & {
  label: any;
  value: any;
  approximateValue?: boolean;
  isLight?: boolean;
  /** use `color` when you want to override the isLight prop */
  color?: Colors;
  gutterRows?: any;
  wrap?: boolean;
  fontSize: 'small' | 'standard' | 'medium' | 'large';
};

SummaryDetails.Row = ({
  label,
  value,
  approximateValue = false,
  isLight,
  color,
  gutterRows,
  wrap,
  fontSize = 'standard',
}: SummaryDetailsRowProps) => {
  const isMobile = useMediaMax(theme.breakpoint.md);

  return (
    <SummaryDetailsRow flexDirection={isMobile && wrap ? 'column' : 'row'} gutterRows={gutterRows}>
      <Text renderAs="span" size={fontSize} color={color ? color : isLight ? 'black70' : 'darkBlue40'}>
        {label}:
      </Text>
      {(!isMobile || !wrap) && (
        <LineWrapper flexGrow={1} display="inline-flex" height={12} alignItems="flex-end">
          &nbsp;
          <Line variant="dashed" width="100%" markerSize="3px 1px" color={isLight ? 'black70' : 'darkBlue40'} />
          &nbsp;
        </LineWrapper>
      )}
      {approximateValue ? (
        <span>
          <Text renderAs="span" color={color ? color : isLight ? 'black90' : 'white'}>
            {'ca '}
          </Text>
          <Text
            renderAs="span"
            size={fontSize}
            fontWeight="semibold"
            color={color ? color : isLight ? 'black90' : 'white'}
          >
            {value}
          </Text>
        </span>
      ) : (
        <Text
          renderAs="span"
          size={fontSize}
          fontWeight="semibold"
          color={color ? color : isLight ? 'black90' : 'white'}
        >
          {value}
        </Text>
      )}
    </SummaryDetailsRow>
  );
};

SummaryDetails.Caption = ({ isLight, children }: any) => (
  <Text size="small" color={isLight ? 'black70' : 'darkBlue40'} marginTop={[20, 30, 30]}>
    {children}
  </Text>
);
